import { useQueryClient } from '@tanstack/react-query';
import { useContext, useState } from 'react';
import { PremiumDataModal } from '@/components/PremiumDataModal/PremiumDataModal';
import { usePremiumDataModal } from '@/components/PremiumDataModal/hooks/usePremiumDataModal';
import { SearchFormsContainer } from '@/components/SearchFormsContainer/SearchFormsContainer';
import {
  SearchFormProps,
  SearchTabData,
} from '@/components/SearchFormsContainer/interfaces';
import { useAppConfigContext } from '@/context/AppConfig';
import { AppConstants, useAppConstantsContext } from '@/context/AppConstants';
import { PremiumPaymentSuccessModal } from '@/reports/shared/PremiumPaymentModal/PremiumPaymentSuccessModal';
import { checkSingleReportVariations } from '@/utils/checkSingleReportVariations';
import {
  FooterContainer,
  FooterHeader,
  FooterSecondaryContainer,
  FooterTab,
  FooterTabs,
} from './components';
import { useAccount } from '../account';

interface FooterSearchProps {
  /** Optional callback to handle successful search form submission */
  onSearchSuccess?: SearchFormProps['onSuccess'];
  /** Optional callback to handle search form submission errors */
  onSearchError?: SearchFormProps['onError'];
}

/**
 * This component provides the footer search functionality displayed in the reports.
 */
export function FooterSearch({
  onSearchSuccess,
  onSearchError,
}: FooterSearchProps) {
  const queryContext = useQueryClient();
  const {
    config: { searchTabs },
  } = useContext(AppConstants);
  const {
    links: { baseUrl },
    variationPlanNames,
    internationalPlanNames,
  } = useAppConstantsContext();

  const { data: account } = useAccount(true);
  const reportVariations =
    account && internationalPlanNames
      ? checkSingleReportVariations(
          account,
          variationPlanNames,
          internationalPlanNames
        )
      : null;
  const isInSingleReportVariation =
    reportVariations?.isInSingleReportVariation ?? false;

  const { trackEvent, routingUtils } = useAppConfigContext();
  const { navigate } = routingUtils;
  const [selectedTabIndex, setValue] = useState(0);
  const [selectedTab, setSelectedTab] = useState<SearchTabData | null>(
    searchTabs[selectedTabIndex]
  );
  const { isModalOpen, closeModal, triggerModal } = usePremiumDataModal();
  const [paymentShowModalNumber, setPaymentShowModalNumber] =
    useState<number>(0);
  const [isSuccessModaVisible, setIsSuccessModaVisible] =
    useState<boolean>(false);

  const openSuccessModal = () => {
    setIsSuccessModaVisible(true);
  };

  const closeModals = () => {
    setIsSuccessModaVisible(false);
    setPaymentShowModalNumber(0);
  };

  const onSuccessPaymentHandler = () => {
    closeModals();
    navigate('/dashboard');
  };

  function setTab(tab: SearchTabData) {
    const isDifferentTab = !selectedTab || tab.id !== selectedTab.id;
    if (!isDifferentTab) return;

    setSelectedTab(tab);
  }

  function openInNewTab(
    newTab: boolean | undefined,
    route: string | undefined
  ) {
    if (newTab && route) {
      window.open(`${baseUrl}${route}`, '_blank', 'noopener,noreferrer');
    }
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    trackEvent(
      `${searchTabs[newValue]?.displayName} search`,
      'navigation click',
      'footer search'
    );
    setValue(newValue);
    setTab(searchTabs[newValue]);
    openInNewTab(
      searchTabs[newValue]?.openInNewTab,
      searchTabs[newValue]?.route
    );
  };

  const handleTabClick = (tab: SearchTabData) => {
    openInNewTab(tab.openInNewTab, tab.route);
    triggerModal(tab.isLocked, isInSingleReportVariation);
  };

  const handleSearchSuccess: SearchFormProps['onSuccess'] = (searchPayload) => {
    onSearchSuccess && onSearchSuccess(searchPayload);
  };

  const handleSearchError: SearchFormProps['onError'] = ((
    error: unknown,
    variables: unknown,
    context: unknown
  ) => {
    queryContext
      ?.getDefaultOptions()
      ?.mutations?.onError?.(error, variables, context);
    onSearchError && onSearchError(error as Error);
  }) as SearchFormProps['onError'];

  const shouldLockSearchForm =
    isInSingleReportVariation && selectedTab?.isLocked;

  return (
    <FooterContainer>
      <FooterSecondaryContainer>
        <FooterHeader variant="h2">Try Another Search</FooterHeader>

        {searchTabs.length > 1 && (
          <FooterTabs value={selectedTabIndex} onChange={handleTabChange}>
            {searchTabs.map((tab, index) => (
              <FooterTab
                label={tab.displayName}
                key={`footer-tab-${index}`}
                onClick={() => handleTabClick(tab)}
              />
            ))}
          </FooterTabs>
        )}

        <SearchFormsContainer
          displayName={selectedTab?.displayName || ''}
          internalTabs={selectedTab?.internalTabs}
          onSearchSuccess={handleSearchSuccess}
          onSearchError={handleSearchError}
          closeForm={() => null}
          placement="footer"
          isSearchFormLocked={shouldLockSearchForm}
        />
      </FooterSecondaryContainer>
      {isInSingleReportVariation && (
        <PremiumDataModal
          isOpen={isModalOpen}
          onClose={closeModal}
          searchType={selectedTab?.displayName}
          paymentShowModalNumber={paymentShowModalNumber}
          setPaymentShowModalNumber={setPaymentShowModalNumber}
          openSuccessModal={openSuccessModal}
        />
      )}
      <PremiumPaymentSuccessModal
        open={isSuccessModaVisible}
        onClose={closeModals}
        onAccept={onSuccessPaymentHandler}
      />
    </FooterContainer>
  );
}
